import React from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
              Promang Documentation Portal
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              Unified documentation for Promang API and Frontend applications.
              Access comprehensive guides, API references, and development resources.
            </p>
            <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
              <div className="rounded-md shadow">
                <Link
                  to="/api-docs"
                  className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 md:py-4 md:text-lg md:px-10"
                >
                  API Documentation
                </Link>
              </div>
              <div className="mt-3 rounded-md shadow sm:mt-0 sm:ml-3">
                <Link
                  to="/frontend-docs"
                  className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-50 md:py-4 md:text-lg md:px-10"
                >
                  Frontend Documentation
                </Link>
              </div>
              <div className="mt-3 rounded-md shadow sm:mt-0 sm:ml-3">
                <Link
                  to="/component-library"
                  className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-slate-700 bg-slate-100 hover:bg-slate-200 md:py-4 md:text-lg md:px-10"
                >
                  Komponentbibliotek
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Cards */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Documentation</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Everything you need to know
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Comprehensive documentation covering all aspects of Promang development.
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {/* API Card */}
              <div className="pt-6">
                <div className="flow-root bg-white rounded-lg px-6 pb-8 shadow-sm border border-gray-200">
                  <div className="-mt-6">
                    <div className="inline-flex items-center justify-center p-3 bg-primary-500 rounded-md shadow-lg">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">API Documentation</h3>
                    <p className="mt-5 text-base text-gray-500">
                      Complete API reference with endpoints, models, and examples. Includes HSE/KS compliance modules.
                    </p>
                    <div className="mt-6">
                      <Link
                        to="/api-docs"
                        className="text-base font-medium text-primary-600 hover:text-primary-500"
                      >
                        View API docs →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Frontend Card */}
              <div className="pt-6">
                <div className="flow-root bg-white rounded-lg px-6 pb-8 shadow-sm border border-gray-200">
                  <div className="-mt-6">
                    <div className="inline-flex items-center justify-center p-3 bg-green-500 rounded-md shadow-lg">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    </div>
                    <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Frontend Documentation</h3>
                    <p className="mt-5 text-base text-gray-500">
                      React components, hooks, state management, and development guidelines for the frontend application.
                    </p>
                    <div className="mt-6">
                      <Link
                        to="/frontend-docs"
                        className="text-base font-medium text-primary-600 hover:text-primary-500"
                      >
                        View frontend docs →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* HSE/KS Card */}
              <div className="pt-6">
                <div className="flow-root bg-white rounded-lg px-6 pb-8 shadow-sm border border-gray-200">
                  <div className="-mt-6">
                    <div className="inline-flex items-center justify-center p-3 bg-red-500 rounded-md shadow-lg">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">HSE/KS Compliance</h3>
                    <p className="mt-5 text-base text-gray-500">
                      ISO 9001:2024 compliant Health, Safety, and Quality management system with full documentation.
                    </p>
                    <div className="mt-6">
                      <Link
                        to="/api-docs"
                        className="text-base font-medium text-primary-600 hover:text-primary-500"
                      >
                        View HSE/KS docs →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* OpenAPI Card */}
              <div className="pt-6">
                <div className="flow-root bg-white rounded-lg px-6 pb-8 shadow-sm border border-gray-200">
                  <div className="-mt-6">
                    <div className="inline-flex items-center justify-center p-3 bg-indigo-500 rounded-md shadow-lg">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9h8M8 13h5m-9 8h16a2 2 0 002-2V5a2 2 0 00-2-2H4a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">OpenAPI Reference</h3>
                    <p className="mt-5 text-base text-gray-500">
                      Generated operation list grouped by tag when the API OpenAPI artifact is synced.
                    </p>
                    <div className="mt-6">
                      <Link
                        to="/api-reference"
                        className="text-base font-medium text-primary-600 hover:text-primary-500"
                      >
                        View OpenAPI →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">Comprehensive Documentation</h2>
            <p className="mt-3 text-xl text-gray-500 sm:mt-4">
              Everything you need to develop, maintain, and extend Promang applications.
            </p>
          </div>
          <dl className="mt-10 text-center sm:max-w-3xl sm:mx-auto sm:grid sm:grid-cols-3 sm:gap-8">
            <div className="flex flex-col">
              <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">API Endpoints</dt>
              <dd className="order-1 text-5xl font-extrabold text-primary-600">150+</dd>
            </div>
            <div className="flex flex-col mt-10 sm:mt-0">
              <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">Components</dt>
              <dd className="order-1 text-5xl font-extrabold text-primary-600">500+</dd>
            </div>
            <div className="flex flex-col mt-10 sm:mt-0">
              <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">Modules</dt>
              <dd className="order-1 text-5xl font-extrabold text-primary-600">25+</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default Home;

